import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { fulfillmentApi } from '../../api/fulfillment-api';
import { ConflictError } from '../../api/errors';
import { PartialFailureNotice } from '../../components/PartialFailureNotice';
import {
  linePicked,
  requirementLabel,
  type AmendLineInput,
  type AmendmentAction,
  type FulfillmentOrder,
  type FulfillmentOrderLine,
} from '../../domain/fulfillment';
import { fulfillmentErrorMessage, isUnknownOutcome } from './error-message';
import { newRequirementRow, requirementRowsError, type RequirementRow } from './requirement-rows';
import { RequirementsEditor } from './RequirementsEditor';
import type { FulfillmentReferences } from './use-fulfillment-references';

/**
 * Changes one line of a released order (tidaltech/koi#8081): reduce it,
 * replace what it asks for, or cancel it. The server swaps the stock
 * reservation in the same step and keeps the reason.
 */
export function AmendLineDialog({
  order,
  line,
  references,
  onClose,
  onDone,
  onReload,
}: {
  order: FulfillmentOrder;
  line: FulfillmentOrderLine;
  references: FulfillmentReferences;
  onClose: () => void;
  onDone: (order: FulfillmentOrder) => void;
  onReload: () => void;
}) {
  const picked = linePicked(line);
  const canReduce = line.requirement.quantity > Math.max(1, picked);
  const canReplace = picked === 0;
  const canCancel = picked === 0 && order.lines.length > 1;
  const [action, setAction] = useState<AmendmentAction>(
    canReduce ? 'REDUCE_QUANTITY' : canReplace ? 'SUBSTITUTE' : 'CANCEL_LINE',
  );
  const [quantity, setQuantity] = useState(String(Math.max(1, picked)));
  const [rows, setRows] = useState<RequirementRow[]>(() => [
    newRequirementRow(line.requirement.type, { ...line.requirement }, line.locationId),
  ]);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<{ text: string; conflict: boolean } | null>(null);
  const [unknown, setUnknown] = useState(false);
  const name = requirementLabel(line.requirement, {
    part: references.partName,
    stock: references.stockName,
  });

  const amend = useMutation({
    mutationFn: (input: AmendLineInput) => fulfillmentApi.amendLine(input),
    onSuccess: onDone,
    onError: (failure: unknown) => {
      if (isUnknownOutcome(failure)) setUnknown(true);
      else
        setError({
          text: fulfillmentErrorMessage(failure, 'The line could not be changed.'),
          conflict: failure instanceof ConflictError,
        });
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    const base = {
      orderId: order.id,
      version: order.version,
      lineId: line.id,
      reason: reason.trim(),
    };
    if (!base.reason) {
      setError({ text: 'Say why the line is changing.', conflict: false });
      return;
    }
    if (action === 'REDUCE_QUANTITY') {
      const qty = Number.parseInt(quantity, 10);
      if (!Number.isInteger(qty) || qty < Math.max(1, picked) || qty >= line.requirement.quantity) {
        setError({
          text: `Choose a quantity from ${Math.max(1, picked)} to ${line.requirement.quantity - 1}.`,
          conflict: false,
        });
        return;
      }
      amend.mutate({ ...base, action, quantity: qty });
    } else if (action === 'SUBSTITUTE') {
      const problem = requirementRowsError(rows);
      const row = rows[0];
      if (problem || !row) {
        setError({ text: problem ?? 'Describe the replacement.', conflict: false });
        return;
      }
      amend.mutate({ ...base, action, requirement: row.requirement, locationId: row.locationId });
    } else {
      amend.mutate({ ...base, action });
    }
  }

  const title = `Change line: ${name}`;
  return (
    <div className="drawer-overlay" role="presentation" onClick={onClose}>
      <form
        className="drawer drawer-wide"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="drawer-heading">
          <h2>{title}</h2>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close change line"
          >
            ×
          </button>
        </div>
        {picked > 0 ? (
          <p className="field-hint">
            {picked} already picked. Remove those picks first to replace or cancel the line.
          </p>
        ) : null}
        <fieldset className="plain-fieldset">
          <legend className="visually-hidden">Change</legend>
          <label className="radio-field">
            <input
              type="radio"
              name="amend-action"
              checked={action === 'REDUCE_QUANTITY'}
              disabled={!canReduce}
              onChange={() => setAction('REDUCE_QUANTITY')}
            />
            Reduce the quantity
          </label>
          {action === 'REDUCE_QUANTITY' ? (
            <label className="quantity-field">
              New quantity (was {line.requirement.quantity})
              <input
                type="number"
                min={Math.max(1, picked)}
                max={line.requirement.quantity - 1}
                step={1}
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
              />
            </label>
          ) : null}
          <label className="radio-field">
            <input
              type="radio"
              name="amend-action"
              checked={action === 'SUBSTITUTE'}
              disabled={!canReplace}
              onChange={() => setAction('SUBSTITUTE')}
            />
            Replace it with something else
          </label>
          {action === 'SUBSTITUTE' ? (
            <RequirementsEditor
              label="Replacement"
              rows={rows}
              onChange={(next) => setRows(next.slice(0, 1))}
              references={references}
              showLocations
              single
            />
          ) : null}
          <label className="radio-field">
            <input
              type="radio"
              name="amend-action"
              checked={action === 'CANCEL_LINE'}
              disabled={!canCancel}
              onChange={() => setAction('CANCEL_LINE')}
            />
            Cancel this line
            {order.lines.length === 1 ? ' (it is the only line; cancel the order instead)' : ''}
          </label>
        </fieldset>
        <label>
          Reason (required)
          <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={2} />
        </label>
        <p className="field-hint">
          The reserved stock is adjusted in the same step, and the change is kept on the order.
        </p>
        {error ? (
          <div role="alert" className="form-error">
            {error.text}
            {error.conflict ? (
              <button type="button" className="link-button" onClick={onReload}>
                Reload order
              </button>
            ) : null}
          </div>
        ) : null}
        {unknown ? <PartialFailureNotice onCheckCurrentState={onReload} /> : null}
        <div className="drawer-actions">
          <button type="button" onClick={onClose}>
            Keep line
          </button>
          <button type="submit" className="primary" disabled={amend.isPending}>
            {amend.isPending ? 'Saving…' : 'Change line'}
          </button>
        </div>
      </form>
    </div>
  );
}
